import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { NgxTgModule } from '@worthy-ventures/metaglotta-ngx';

/**
 * The single section nav for every signed-in role — the dashboard tabs and the
 * admin tabs in one bar (was two separate bars, app-admin-nav and
 * app-dashboard-nav, each visible only on its own pages).
 *
 * Each getter below mirrors the route guard for the tabs it controls, so a tab
 * is shown only where navigating to it actually succeeds. Keep them in step: a
 * looser getter means a dead tab, a stricter one hides a page the user may use.
 */
@Component({
    selector: 'app-admin-nav',
    imports: [RouterLink, RouterLinkActive, NgxTgModule],
    templateUrl: './admin-nav.component.html',
    styleUrl: './admin-nav.component.scss',
})
export class AdminNavComponent implements AfterViewInit, OnDestroy {
    private session = inject(SessionService);

    /**
     * The bar is one scrolling row rather than a wrapping block.
     *
     * An admin sees ten destinations, which on a phone wrapped to three rows and
     * pushed the page's own content most of a screen down. It scrolls at every
     * width rather than under a breakpoint: where the tabs already fit, there is
     * nothing to scroll and nothing to fade, so it looks exactly as it did.
     *
     * The two flags drive the edge fades — an affordance, so they have to mean
     * "there is more that way" rather than being painted on permanently.
     */
    scrolledStart = false;
    scrolledEnd = false;

    @ViewChild('nav') private navEl?: ElementRef<HTMLElement>;

    /** Tabs depend on role and labels on language, so the row's width is not
     *  settled at first paint. */
    private resizeObserver?: ResizeObserver;

    ngAfterViewInit(): void {
        const el = this.navEl?.nativeElement;
        if (!el) return;

        // Centre the tab you are actually on. Ten tabs run about two and a half
        // screens at phone width, so landing on a later one with the row parked
        // at the start reads as the current section missing from the nav.
        //
        // scrollLeft rather than scrollIntoView(): that one can scroll the page
        // vertically as well, which on arrival would move the content out from
        // under the reader.
        const active = el.querySelector('a.active') as HTMLElement | null;
        if (active) {
            el.scrollLeft = active.offsetLeft - (el.clientWidth - active.clientWidth) / 2;
        }

        this.measure();
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.measure());
            this.resizeObserver.observe(el);
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    onScroll(): void {
        this.measure();
    }

    /** A few pixels of slack on both comparisons: scrollLeft is fractional on
     *  zoomed and hidpi displays, so an exact test can leave a fade up at the
     *  very end of the scroll. */
    private measure(): void {
        const el = this.navEl?.nativeElement;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        this.scrolledStart = el.scrollLeft > 4;
        this.scrolledEnd = max > 4 && el.scrollLeft < max - 4;
    }

    /** Account Details is reachable by anyone signed in — the page itself only
     *  checks isLoggedIn, and adapts for cloud customers. */
    get isLoggedIn(): boolean {
        return this.session.isLoggedIn();
    }

    /** Mirrors adminGuard. NOTE: a cloud session can carry isAdmin (legacy
     *  is_admin, or the FYES_FORCE_ADMIN dev flag), and the backend's
     *  fyesAdminRequired does not require isLocal either. */
    get isAdmin(): boolean {
        return this.session.isLoggedIn() && this.session.isAdmin();
    }

    /**
     * Mirrors dashboardGuard: `isLocal && canAccessOrders`. The isLocal half
     * matters — the dashboard acts on Fyes_AdminUsers identities (assignedTo,
     * resolvedBy), which a cloud session has no row for, so without it a cloud
     * admin would see Overview/Orders tabs that the guard then bounces.
     */
    get canAccessOrders(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && this.session.canAccessOrders();
    }

    /**
     * The file-portal user: a local account with neither admin rights nor orders
     * access. Sales (canAccessOrders) is deliberately excluded — they get the
     * dashboard and nothing else.
     */
    get isFilePortalUser(): boolean {
        return this.session.isLoggedIn() && this.session.isLocal() && !this.session.canAccessOrders();
    }

    /** Cloud gateway customer: their own gateway list, never the admin tabs. */
    get isCloudCustomer(): boolean {
        return this.session.isLoggedIn() && !this.session.isLocal() && !this.session.isAdmin();
    }
}
